/**
 * CFS Google reviews slider (sections/cfs-google-reviews.liquid; owner, 14 Oct 2026).
 * A scroll-snap row of review cards with previous/next arrows. It moves on by one card every few
 * seconds while on screen, and pauses while the shopper points at it, touches it, or has focus in it.
 * No autoplay with reduced motion. Long reviews are clamped with a "Read more" toggle.
 */
const INTERVAL_MS = 5000;
const RESUME_AFTER_MS = 8000;
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class CfsReviewsSlider extends HTMLElement {
  #timer = 0;
  #held = false;
  #visible = false;
  #resumeTimer = 0;

  connectedCallback() {
    this.track = /** @type {HTMLElement} */ (this.querySelector('[data-track]'));
    this.prev = /** @type {HTMLButtonElement} */ (this.querySelector('[data-prev]'));
    this.next = /** @type {HTMLButtonElement} */ (this.querySelector('[data-next]'));
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.#step(-1, true));
    this.next?.addEventListener('click', () => this.#step(1, true));
    this.track.addEventListener('scroll', this.#updateArrows, { passive: true });

    this.addEventListener('pointerenter', () => this.#hold(true));
    this.addEventListener('pointerleave', () => this.#hold(false));
    this.addEventListener('focusin', () => this.#hold(true));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node | null} */ (event.relatedTarget))) this.#hold(false);
    });
    this.track.addEventListener('touchstart', () => this.#pauseForAWhile(), { passive: true });

    // First run on the observer's first call, after the browser's own layout (no forced layout on load)
    new ResizeObserver(() => {
      const scrollable = this.#scrollable(); // read before #setUpMore changes anything
      this.#setUpMore();
      this.toggleAttribute('data-static', !scrollable);
    }).observe(this.track);

    new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
      this.#schedule();
    }).observe(this);
    REDUCED_MOTION.addEventListener('change', () => this.#schedule());
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    clearTimeout(this.#resumeTimer);
  }

  /** Width of one card plus the gap: one step of the arrows */
  get #stepSize() {
    const slide = this.track.querySelector('li');
    if (!slide) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  /** @param {number} direction @param {boolean} [byShopper] */
  #step(direction, byShopper = false) {
    if (byShopper) this.#pauseForAWhile();
    const max = this.track.scrollWidth - this.track.clientWidth;
    const behavior = REDUCED_MOTION.matches ? 'instant' : 'smooth';
    if (direction > 0 && this.track.scrollLeft >= max - 4) {
      this.track.scrollTo({ left: 0, behavior }); // back to the first review
    } else if (direction < 0 && this.track.scrollLeft <= 4) {
      this.track.scrollTo({ left: max, behavior });
    } else {
      this.track.scrollBy({ left: direction * this.#stepSize, behavior });
    }
  }

  #scrollable() {
    return this.track.scrollWidth > this.track.clientWidth + 4;
  }

  #updateArrows = () => {
    this.toggleAttribute('data-static', !this.#scrollable());
  };

  /** @param {boolean} held */
  #hold(held) {
    this.#held = held;
    this.#schedule();
  }

  #pauseForAWhile() {
    this.#held = true;
    this.#schedule();
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = window.setTimeout(() => {
      if (!this.matches(':hover, :focus-within')) this.#hold(false);
    }, RESUME_AFTER_MS);
  }

  #schedule() {
    clearInterval(this.#timer);
    if (this.#held || !this.#visible || REDUCED_MOTION.matches || this.hasAttribute('data-static')) return;
    this.#timer = window.setInterval(() => this.#step(1), INTERVAL_MS);
  }

  /** "Read more" only on reviews the clamp actually cuts */
  #setUpMore() {
    // All the measuring first, then the changes: a change between two reads makes the browser lay out again
    /** @type {[HTMLButtonElement, boolean][]} */
    const toggles = [];
    for (const card of this.track.querySelectorAll('.cfs-gcard')) {
      const text = /** @type {HTMLElement} */ (card.querySelector('[data-text]'));
      const more = /** @type {HTMLButtonElement} */ (card.querySelector('[data-more]'));
      if (!text || !more) continue;
      if (!more.dataset.bound) {
        more.dataset.bound = '1';
        more.addEventListener('click', () => {
          const open = card.classList.toggle('is-open');
          more.textContent = open ? 'Show less' : 'Read more';
          more.setAttribute('aria-expanded', String(open));
          this.#pauseForAWhile();
        });
      }
      if (card.classList.contains('is-open')) continue;
      // Clamped to 6 lines, 4 in the compact band (cfs-reviews.css): show the toggle only when the full text runs longer
      const style = getComputedStyle(text);
      const lineHeight = parseFloat(style.lineHeight) || 22;
      const lines = parseInt(style.getPropertyValue('-webkit-line-clamp'), 10) || 6;
      toggles.push([more, text.scrollHeight <= lineHeight * lines + 2]);
    }
    for (const [more, hidden] of toggles) if (more.hidden !== hidden) more.hidden = hidden;
  }
}

if (!customElements.get('cfs-reviews-slider')) customElements.define('cfs-reviews-slider', CfsReviewsSlider);
